import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, History, FileSignature, AlertTriangle, Lock } from "lucide-react";

const STATUSES = ["draft", "internal_review", "sent", "client_reviewing", "signed", "active", "expired", "terminated"];
const LABEL = { draft: "Draft", internal_review: "Internal Review", sent: "Sent", client_reviewing: "Client Reviewing", signed: "Signed", active: "Active", expired: "Expired", terminated: "Terminated" };
const TONE = { draft: "bg-muted text-foreground", internal_review: "bg-amber-100 text-amber-800", sent: "bg-sky-100 text-sky-800", client_reviewing: "bg-sky-100 text-sky-800", signed: "bg-emerald-100 text-emerald-800", active: "bg-[#255058]/10 text-[#255058]", expired: "bg-rose-100 text-rose-800", terminated: "bg-rose-100 text-rose-800" };

const EMPTY = {
  name: "", agreement_type: "MSA", fee_percent: "", flat_fee: "", currency: "USD",
  payment_terms: "", invoice_trigger: "", invoice_due_days: "", replacement_period: "",
  guarantee_period: "", guarantee_conditions: "", special_terms: "", effective_date: "",
  expiry: "", msa_document_id: "", signed_document_id: "", approved_by: "", approved_at: "",
  approval_notes: "",
};

export default function AgreementsPanel({ clientId, currency, canWrite }) {
  const [items, setItems] = useState(null);
  const [docs, setDocs] = useState([]);
  const [sheet, setSheet] = useState(null); // {mode:'create'|'edit'|'revise', agreement}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [historyOf, setHistoryOf] = useState(null);
  const [statusFor, setStatusFor] = useState(null);
  const [statusValue, setStatusValue] = useState("draft");
  const [statusReason, setStatusReason] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/clients/${clientId}/agreements`); setItems(data.items || []); }
    catch (e) { toast.error(apiErr(e)); setItems([]); }
    try { const { data } = await api.get("/files", { params: { record_type: "client", record_id: clientId } }); setDocs(data.items || []); }
    catch { setDocs([]); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const open = (mode, agreement) => {
    setSheet({ mode, agreement });
    setForm(agreement ? { ...EMPTY, ...Object.fromEntries(Object.entries(agreement).map(([k, v]) => [k, v ?? ""])) } : { ...EMPTY, currency: currency || "USD" });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Agreement name is required"); return; }
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = { ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])) };
    ["fee_percent", "flat_fee", "invoice_due_days"].forEach((k) => { payload[k] = num(form[k]); });
    delete payload.id; delete payload.status; delete payload.version; delete payload.status_history;
    delete payload.read_only; delete payload.is_current; delete payload.previous_agreement_id;
    delete payload.superseded_by_id; delete payload.signed_document_missing; delete payload.created_at;
    delete payload.updated_at; delete payload.created_by; delete payload.legacy_status;
    setSaving(true);
    try {
      if (sheet.mode === "create") await api.post(`/clients/${clientId}/agreements`, payload);
      else if (sheet.mode === "edit") await api.put(`/clients/${clientId}/agreements/${sheet.agreement.id}`, payload);
      else await api.post(`/clients/${clientId}/agreements/${sheet.agreement.id}/revise`, payload);
      toast.success(sheet.mode === "revise" ? "Revision created" : "Agreement saved");
      setSheet(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const applyStatus = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/agreements/${statusFor.id}/status`, { status: statusValue, reason: statusReason || null });
      toast.success("Status updated"); setStatusFor(null); setStatusReason(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!items) return <Card className="p-5 text-sm text-muted-foreground">Loading agreements…</Card>;

  return (
    <div className="space-y-3" data-testid="agreements-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-semibold"><Lock className="h-4 w-4 text-[#255058]" /> Agreements</div>
        {canWrite && <Button size="sm" className="gap-1.5 bg-[#255058] text-white hover:bg-[#1c3d43]" onClick={() => open("create")} data-testid="agreement-create">
          <Plus className="h-4 w-4" /> New Agreement</Button>}
      </div>
      {!items.length && <Card className="p-6 text-center text-sm text-muted-foreground">No agreements yet.</Card>}
      <div className="space-y-2">
        {items.map((a) => (
          <Card key={a.id} className="p-4" data-testid={`agreement-row-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <FileSignature className="h-4 w-4 text-[#255058]" />
                  <span className="font-medium">{a.name}</span>
                  <Badge variant="outline" className="text-[10px]">v{a.version || 1}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${TONE[a.status] || ""}`}>{LABEL[a.status] || a.status}</Badge>
                  {a.read_only && <Badge variant="outline" className="text-[10px]">Read-only</Badge>}
                  {a.signed_document_missing && <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-[10px] text-amber-800"><AlertTriangle className="h-3 w-3" /> Signed document missing</Badge>}
                </div>
                <div className="mt-2 grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-3">
                  <span>Fee: {a.fee_percent != null ? `${a.fee_percent}%` : a.flat_fee != null ? money(a.flat_fee, a.currency) : "—"}</span>
                  <span>Payment terms: {a.payment_terms || "—"}</span>
                  <span>Invoice trigger: {a.invoice_trigger || "—"}</span>
                  <span>Invoice due: {a.invoice_due_days != null ? `${a.invoice_due_days} days` : "—"}</span>
                  <span>Guarantee: {a.guarantee_period || "—"}</span>
                  <span>Replacement: {a.replacement_period || "—"}</span>
                  <span>Effective: {a.effective_date ? fmtDate(a.effective_date) : "—"}</span>
                  <span>Expires: {a.expiry ? fmtDate(a.expiry) : "—"}</span>
                  <span>Approved by: {a.approved_by || "—"}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setHistoryOf(a)} data-testid={`agreement-history-${a.id}`}><History className="h-3.5 w-3.5" /> History</Button>
                {canWrite && !a.read_only && <>
                  <Button size="sm" variant="outline" onClick={() => open("edit", a)} data-testid={`agreement-edit-${a.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => { setStatusFor(a); setStatusValue(a.status); setStatusReason(""); }} data-testid={`agreement-status-${a.id}`}>Status</Button>
                  <Button size="sm" variant="outline" onClick={() => open("revise", a)} data-testid={`agreement-revise-${a.id}`}>Revise</Button>
                </>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Sheet open={!!sheet} onOpenChange={(o) => !o && setSheet(null)}>
        <SheetContent className="w-full overflow-y-auto scrollbar-thin sm:max-w-xl" data-testid="agreement-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">{sheet?.mode === "create" ? "New Agreement" : sheet?.mode === "revise" ? `Revise — v${(sheet?.agreement?.version || 1) + 1}` : "Edit Agreement"}</SheetTitle>
            <SheetDescription>Commercial terms are restricted to authorized users. Revisions keep older versions read-only.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 grid gap-4 pb-8 sm:grid-cols-2">
            <F label="Name *"><Input data-testid="ag-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Type">
              <Select value={form.agreement_type || "MSA"} onValueChange={(v) => setForm({ ...form, agreement_type: v })}>
                <SelectTrigger data-testid="ag-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["MSA", "SOW", "Fee Agreement", "NDA", "Addendum"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Fee (%)"><Input type="number" data-testid="ag-fee" value={form.fee_percent} onChange={(e) => setForm({ ...form, fee_percent: e.target.value })} /></F>
            <F label="Flat fee"><Input type="number" value={form.flat_fee} onChange={(e) => setForm({ ...form, flat_fee: e.target.value })} /></F>
            <F label="Currency">
              <Select value={form.currency || "USD"} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["USD", "CAD", "AED", "SAR", "INR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Payment terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></F>
            <F label="Invoice trigger">
              <Select value={form.invoice_trigger || ""} onValueChange={(v) => setForm({ ...form, invoice_trigger: v })}>
                <SelectTrigger data-testid="ag-invoice-trigger"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["On offer acceptance", "On joining", "On start date", "After guarantee period", "Monthly (contract)"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Invoice due days"><Input type="number" value={form.invoice_due_days} onChange={(e) => setForm({ ...form, invoice_due_days: e.target.value })} /></F>
            <F label="Replacement period"><Input value={form.replacement_period} onChange={(e) => setForm({ ...form, replacement_period: e.target.value })} /></F>
            <F label="Guarantee period"><Input value={form.guarantee_period} onChange={(e) => setForm({ ...form, guarantee_period: e.target.value })} /></F>
            <F label="Effective date"><Input type="date" value={(form.effective_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></F>
            <F label="Expiry date"><Input type="date" data-testid="ag-expiry" value={(form.expiry || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expiry: e.target.value })} /></F>
            <F label="MSA / SOW document">
              <Select value={form.msa_document_id || "none"} onValueChange={(v) => setForm({ ...form, msa_document_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ag-msa-doc"><SelectValue placeholder="Select uploaded file" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not attached</SelectItem>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.original_filename}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Signed document">
              <Select value={form.signed_document_id || "none"} onValueChange={(v) => setForm({ ...form, signed_document_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ag-signed-doc"><SelectValue placeholder="Select uploaded file" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not attached</SelectItem>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.original_filename}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Approved by"><Input value={form.approved_by} onChange={(e) => setForm({ ...form, approved_by: e.target.value })} /></F>
            <F label="Approved at"><Input type="date" value={(form.approved_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, approved_at: e.target.value })} /></F>
            <div className="sm:col-span-2"><F label="Guarantee conditions"><Textarea rows={2} value={form.guarantee_conditions} onChange={(e) => setForm({ ...form, guarantee_conditions: e.target.value })} /></F></div>
            <div className="sm:col-span-2"><F label="Special terms"><Textarea rows={2} value={form.special_terms} onChange={(e) => setForm({ ...form, special_terms: e.target.value })} /></F></div>
            <div className="sm:col-span-2"><F label="Approval notes"><Textarea rows={2} value={form.approval_notes} onChange={(e) => setForm({ ...form, approval_notes: e.target.value })} /></F></div>
            <div className="sm:col-span-2 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setSheet(null)}>Cancel</Button>
              <Button className="flex-1 bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={saving} onClick={save} data-testid="ag-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!statusFor} onOpenChange={(o) => !o && setStatusFor(null)}>
        <SheetContent className="w-full sm:max-w-md" data-testid="agreement-status-sheet">
          <SheetHeader><SheetTitle className="font-display">Change agreement status</SheetTitle>
            <SheetDescription>Every change is appended to the agreement history.</SheetDescription></SheetHeader>
          <div className="mt-6 space-y-4">
            <F label="Status">
              <Select value={statusValue} onValueChange={setStatusValue}>
                <SelectTrigger data-testid="ag-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label={`Reason${["expired", "terminated"].includes(statusValue) ? " *" : ""}`}>
              <Textarea rows={3} data-testid="ag-status-reason" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} />
            </F>
            <Button className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={saving} onClick={applyStatus} data-testid="ag-status-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update status"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!historyOf} onOpenChange={(o) => !o && setHistoryOf(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="agreement-history-sheet">
          <SheetHeader><SheetTitle className="font-display">{historyOf?.name} · v{historyOf?.version || 1}</SheetTitle>
            <SheetDescription>Append-only status history.</SheetDescription></SheetHeader>
          <div className="mt-6 space-y-3">
            {(historyOf?.status_history || []).map((h, i) => (
              <div key={i} className="rounded-lg border border-border p-3 text-sm">
                <div className="font-medium">{LABEL[h.status] || h.status}{h.from ? ` (from ${LABEL[h.from] || h.from})` : ""}</div>
                <div className="text-xs text-muted-foreground">{h.by} · {fmtDate(h.at)}</div>
                {h.reason && <div className="mt-1 text-xs">{h.reason}</div>}
              </div>
            ))}
            {historyOf?.previous_agreement_id && <div className="text-xs text-muted-foreground">Revision of an earlier version (kept read-only).</div>}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
